import {
  Entities,
  EntityKind,
  matcher,
  TDamage,
  ExtendedBlock,
  LazorToggle,
} from "../entityKinds";
import {
  GameEvents,
  SceneName,
  GAME_VARIABLES,
  DEV_MODE,
  BOARD_SIZE,
} from "../../constants";
import GameState, { GameStateObj, getXYindex } from "../gameState";
import { RemovedBecause } from "./overlap";
import { GameTurnLogic } from "../GameTurnLogic";
import { getDevEmitter, dev_event } from "../../../devUI/devEvents";
import { GlobalEvents, EGlobalEvent } from "../../plugins/GlobalGameEvents";
import anim from "../../artConfig.json";

const extendedBodiesMap: Map<number, ExtendedBlock[]> = new Map();

const endOfTurnHookMatcher = (
  cell: Entities[],
  events: Phaser.Events.EventEmitter,
  GameState: GameState,
  idx: number,
  state: GameStateObj
) =>
  matcher<void>({
    [EntityKind.StaticWall]: () => {},
    [EntityKind.Crate]: () => {},
    [EntityKind.ExtendedBlock]: () => {},
    [EntityKind.Empty]: () => {},
    [EntityKind.PlayerBullet]: () => {},
    [EntityKind.MeleeEnemy]: (e) => {
      e.exhaustedTurn = false;
    },
    [EntityKind.PressurePlate]: (e) => {
      if (
        cell.some((e) =>
          [
            EntityKind.Crate,
            EntityKind.PlayerBody,
            EntityKind.MeleeEnemy,
            EntityKind.ExtendedBlock,
          ].includes(e.kind)
        )
      ) {
        events.emit(GameEvents.PressurePlateState, true, {
          triggerId: e.triggerId,
          fromX: e.x,
          fromY: e.y,
        });
      } else {
        events.emit(GameEvents.PressurePlateState, false, {
          triggerId: e.triggerId,
          fromX: e.x,
          fromY: e.y,
        });
      }
    },
    [EntityKind.LazorToggle]: (e) => {
      if (cell.some((e) => [EntityKind.PlayerBullet].includes(e.kind))) {
        const toggle = state.board[idx].find(({ id }) => id) as LazorToggle;
        if (toggle === undefined) {
          throw new Error("we lost the lazor toggle!");
        }
        events.emit(GameEvents.OneShotFX, {
          x: e.x * BOARD_SIZE,
          y: e.y * BOARD_SIZE,
          key: anim.animations.lazor_toggle_FX.key,
          playLaserToggle: true,
        });

        toggle.isActive = !toggle.isActive;

        events.emit(GameEvents.PressurePlateState, toggle.isActive, {
          triggerId: e.triggerId,
          fromX: e.x,
          fromY: e.y,
          force: true,
        });
      } else {
        events.emit(GameEvents.PressurePlateState, e.isActive, {
          triggerId: e.triggerId,
          fromX: e.x,
          fromY: e.y,
          force: false,
        });
      }
    },
    [EntityKind.Door]: () => {},
    [EntityKind.PlayerGun]: () => {},
    [EntityKind.PlayerBody]: () => {
      if (
        cell.some(({ kind }) =>
          [
            EntityKind.Empty,
            EntityKind.MeleeEnemy,
            EntityKind.TDamage,
            EntityKind.PlayerBullet,
          ].includes(kind)
        )
      ) {
        if (cell.some(({ kind }) => kind === EntityKind.Empty)) {
          events.emit(GameEvents.PlayerDied, { reason: RemovedBecause.Fallen });
        } else {
          events.emit(GameEvents.PlayerDied, { reason: RemovedBecause.Shot });
        }
      }
    },
    [EntityKind.TDamage]: () => {},
    [EntityKind.WinLevel]: (e) => {
      if (cell.some(({ kind }) => kind === EntityKind.PlayerBody)) {
        if (DEV_MODE) {
          getDevEmitter()?.emit(dev_event.STOP_RECORDING);
          events.emit(GameEvents.PlayerWonLevel, {
            lvlKey: e.transitionToLevelKey,
            restartPressed: false,
          });
        } else {
          GlobalEvents.emit(EGlobalEvent.WonLevel);
        }
      }
    },
  });

function getAdjacent(x: number, y: number) {
  return [
    [x + 1, y],
    [x - 1, y],
    [x, y + 1],
    [x, y - 1],
  ];
}

function recalculateConnections(
  board: Entities[][],
  setId: number,
  x: number,
  y: number,
  sameGroup: Set<number>
) {
  const idx = getXYindex(x, y);
  if (idx < 0 || idx >= board.length) {
    return;
  }
  let foundBlock = false;
  for (const entity of board[idx]) {
    if (
      entity.kind === EntityKind.ExtendedBlock &&
      sameGroup.has(entity.id) &&
      entity.sharedBlockId === null
    ) {
      entity.sharedBlockId = setId;
      foundBlock = true;
    }
  }
  if (!foundBlock) {
    return;
  }
  for (const [x2, y2] of getAdjacent(x, y)) {
    recalculateConnections(board, setId, x2, y2, sameGroup);
  }
}

export const endOfTurnHook = (
  events: Phaser.Events.EventEmitter,
  GameState: GameState,
  gameLogic: GameTurnLogic
) => {
  // HANDLE CONNECTED BODIES
  GameState.mutateGameState((state) => {
    extendedBodiesMap.clear();
    state.board.forEach((cell) =>
      cell.forEach((e) => {
        if (e.kind === EntityKind.ExtendedBlock) {
          const { sharedBlockId } = e;
          if (extendedBodiesMap.has(sharedBlockId)) {
            extendedBodiesMap.get(sharedBlockId)?.push(e);
          } else {
            extendedBodiesMap.set(sharedBlockId, [e]);
          }
        }
      })
    );
    // HANDLE disconnecting blocks
    for (const bodies of extendedBodiesMap.values()) {
      for (const body of bodies) {
        body.sharedBlockId = null;
      }
    }
    let i = 1;
    console.log(extendedBodiesMap);
    for (const bodies of extendedBodiesMap.values()) {
      const sameGroup = new Set(bodies.map(({ id }) => id));
      for (const body of bodies) {
        recalculateConnections(state.board, i, body.x, body.y, sameGroup);
        i++;
      }
    }
    // End handle disconnecting blocks
  });
  events?.emit(GameEvents.AddAndRemoveEntities);
  GameState.mutateGameState((state) => {
    extendedBodiesMap.clear();
    state.board.forEach((cell) =>
      cell.forEach((e) => {
        if (e.kind === EntityKind.ExtendedBlock) {
          const { sharedBlockId } = e;
          if (extendedBodiesMap.has(sharedBlockId)) {
            extendedBodiesMap.get(sharedBlockId)?.push(e);
          } else {
            extendedBodiesMap.set(sharedBlockId, [e]);
          }
        }
      })
    );
    for (const bodies of extendedBodiesMap.values()) {
      if (
        bodies.every((body) => {
          const idx = getXYindex(body.x, body.y);
          return state.board[idx].some((e) => e.kind === EntityKind.Empty);
        })
      ) {
        for (const body of bodies) {
          extendedBodiesMap.delete(body.sharedBlockId);
          body.entity?.()._removeFromBoard(RemovedBecause.Fallen);
        }
      }
    }
    // FINISH HANDLING CONNECTED BODIES

    for (let j = 0; j < state.board.length; j++) {
      // Do overlaps before we clean things up because we want to
      // ensure damange is applied before it is cleaned up.
      const entities = state.board[j];
      for (let i = 0; i < entities.length; i++) {
        endOfTurnHookMatcher(
          entities,
          events,
          GameState,
          j,
          state
        )(entities[i]);
      }

      // Here we filter everything but only do the assignment
      // if the length changes. This ensures we aren't storing
      // reassignment when no change occurred.
      const new_cell = state.board[j].filter((e) => {
        const kind = e.kind;
        return (
          (kind !== EntityKind.PlayerGun &&
            kind !== EntityKind.TDamage &&
            kind !== EntityKind.PlayerBullet &&
            kind !== EntityKind.PlayerBody) ||
          (kind === EntityKind.TDamage && (e as TDamage).turns_left > 0)
        );
      });
      if (new_cell.length !== state.board[j].length) {
        state.board[j] = new_cell;
      }
    }
  });
};
